import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import {
  orderBy,
  startCase,
  isArray,
  get,
  isEmpty,
  capitalize,
  isString,
  isNumber,
} from 'lodash';
import {
  PencilStatus,
  FinanceType,
  FuelEfficiencyUnit,
  FinanceProvider,
} from '@upstart/harmony/lib/constants';
import { fmtPercent } from '@upstart/harmony/lib/formatter';
import { getFinanceResponseTotalAPR } from '@upstart/harmony/lib/payment-utils';
import { isDefined } from '@upstart/harmony/lib/legos/utils/tsutils';
import PropTypes from 'prop-types';
import Input from '../Input';
import { getFinanceApplicationForResponse } from '../../../util/pencilUtils';
import './Notes.scss';
import ReviewRicButton from '../../../containers/PencilEditor/RICForm/ReviewRicButton';
import {
  isDMSFlowEnabled as isDMSFlowEnabledUtil,
  isSentToDMS,
} from '../../../util/dmsUtils';

function Notes(props) {
  const notesEnd = useRef(null);
  const {
    deal,
    dealer,
    pencilId,
    onPushToDMS,
    showOfferAndRicTab,
    isEmbeddedView,
  } = props;
  const scrollToBottom = () => {
    if (notesEnd.current) {
      notesEnd.current.scrollTop = notesEnd.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [pencilId, props.note, props.pencils]);

  const renderIncentivesUpdate = value => {
    if ((value.new || []).length === 0) {
      return (
        <div>
          <div className="incentives-cleared">Incentives cleared</div>
        </div>
      );
    }
    if ((value.old || []).length !== 0) {
      return (
        <div>
          <div className="notes-desc">
            Incentives changed from{' '}
            {value.old.map(program => program.description).join(', ')} to{' '}
            <span className="new-value">
              {value.new.map(program => program.description).join(', ')}
            </span>
          </div>
        </div>
      );
    }
    return (
      <div>
        <div className="notes-desc">
          Incentives: {value.new.map(program => program.description).join(', ')}
        </div>
      </div>
    );
  };

  const isValidChangeLogUpdate = update => {
    if (!update) {
      return false;
    }
    // only support rendering strings and numbers, ignore other values as they will cause a crash
    return [update.old, update.new]
      .filter(v => !!v)
      .every(v => isString(v) || isNumber(v));
  };

  const renderChangeMadeBy = changeMadeBy => {
    if (!changeMadeBy) {
      return null;
    }
    return (
      <span>
        {' '}
        and the change was made by{' '}
        <span className="change-made-by">{changeMadeBy}</span>
      </span>
    );
  };

  const renderUpdate = (key, value) => {
    if (key === 'programs') return renderIncentivesUpdate(value);
    if (!isValidChangeLogUpdate(value)) return null;
    const isOldValueDefined = isDefined(value.old);
    return (
      <div>
        {isOldValueDefined && (
          <div className="notes-desc">
            {startCase(key)} <span className="old-value">{value.old}</span>{' '}
            changed to <span className="new-value">{value.new}</span>
            {renderChangeMadeBy(value.changeMadeBy)}
          </div>
        )}
        {!isOldValueDefined && (
          <div className="notes-desc">
            {startCase(key)}: {value.new}
            {renderChangeMadeBy(value.changeMadeBy)}
          </div>
        )}
      </div>
    );
  };

  const renderLogItem = item => (
    <div>
      {item && item?.groupName && (
        <div className="notes-subtitle">{startCase(item?.groupName)}</div>
      )}
      {item &&
        Object.keys(item?.updates).map(key =>
          renderUpdate(key, item.updates[key]),
        )}
      <br />
    </div>
  );

  const renderChangeLog = changeLog => (
    <div>
      {Object.keys(changeLog).map(key => (
        <div>
          <div className="notes-title">{startCase(key)}</div>
          {isArray(changeLog[key]) &&
            changeLog[key].map(item => renderLogItem(item))}
          {!isArray(changeLog[key]) && renderLogItem(changeLog[key])}
        </div>
      ))}
    </div>
  );

  const renderAcceptedNote = pencil => {
    const financeType = get(pencil, 'selectedFinanceType');
    const hasDealPushedToCrm = pencil?.pushedToCrmAt || false;
    return (
      <div className="accepted-note">
        <div className="accepted-note-timestamp">
          {moment(pencil.updatedAt).format('MM-DD-YYYY h:mm a')}
        </div>

        <div className="accepted-note-text">
          <div className="notes-title">Finance Offer Accepted!</div>
          <div className="notes-subtitle">{startCase(financeType)}</div>
          <div className="notes-desc">
            {financeType === FinanceType.PURCHASE && (
              <div>
                <div>{get(pencil, 'selectedOffer.months')} Months</div>
                <div>
                  {(get(pencil, 'selectedOffer.apr', 0) * 100).toFixed(2)}% APR
                </div>
              </div>
            )}
            {financeType === FinanceType.LEASE && (
              <div>
                <div>{get(pencil, 'selectedOffer.months')} Months</div>
                <div>
                  {get(pencil, 'selectedOffer.mileage')}{' '}
                  {get(dealer, 'localizationConfig.fuelEfficiencyUnit') ===
                  FuelEfficiencyUnit.L_100KM
                    ? 'Kilometers'
                    : 'Miles'}
                </div>
              </div>
            )}
          </div>
          {hasDealPushedToCrm && (
            <div className="deal-note">Deal pushed to CRM</div>
          )}
        </div>
      </div>
    );
  };

  const renderFinanceResponse = pencil => {
    const response = pencil.selectedFinanceResponse;
    const selectedFinanceApplication = getFinanceApplicationForResponse(
      pencil,
      pencil?.selectedFinanceResponse,
    );
    const isUpstartOffer =
      selectedFinanceApplication?.provider === FinanceProvider.UPSTART;
    const isDMSFlowEnabled = isDMSFlowEnabledUtil(dealer);
    const sentToDMS = isSentToDMS(deal);
    const showDMSButtonInNotes =
      isDefined(deal?.selectedFinanceResponseId) &&
      deal?.selectedFinanceResponseId === pencil?.selectedFinanceResponse.id;

    return (
      <div className="accepted-note">
        <div className="notes-title">Finance Offer Accepted!</div>
        <div className="notes-subtitle">
          {capitalize(response.financeSourceName)}
        </div>
        <div className="notes-desc">
          <div>{response.data?.months || 0} Months</div>
          <div>{fmtPercent(getFinanceResponseTotalAPR(response))} APR</div>
        </div>
        {selectedFinanceApplication && showDMSButtonInNotes && (
          <div className="pt-10 pb-10">
            <div className="pb-10 notes-selected-term-message">
              <i className="glyphicon glyphicon-ok pr-10" />
              Customer selected finance terms.
            </div>
            <ReviewRicButton
              dmsType={dealer?.dmsType}
              dealId={deal?.id}
              financeResponseId={pencil?.selectedFinanceResponse?.id}
              sentToDMS={sentToDMS}
              isUpstartOffer={isUpstartOffer}
              isDMSFlowEnabled={isDMSFlowEnabled}
              onPushToDMS={onPushToDMS}
              showOfferAndRicTab={showOfferAndRicTab}
            />
          </div>
        )}
      </div>
    );
  };

  const showPencilNote = pencil => {
    // REL-5008: To avoid rendering an empty div element, check for existence of properties beforehand
    const showManagerNote = get(pencil, 'managerNote.note');
    const showChangeLog =
      !isEmpty(pencil.changeLog) && !pencil.changeLog.resettedAllChanges;
    const notification = pencil.notification;
    const salesPersonNote = get(pencil, 'salesPersonNote.note');
    const managerNote = get(pencil, 'managerNote.note');
    const hasAcceptedStatus = pencil?.status === PencilStatus.ACCEPTED;
    return (
      notification ||
      salesPersonNote ||
      managerNote ||
      showManagerNote ||
      showChangeLog ||
      hasAcceptedStatus
    );
  };

  const renderNote = pencil => {
    const showManagerNote = get(pencil, 'managerNote.note');
    const showChangeLog =
      !isEmpty(pencil.changeLog) && !pencil.changeLog.resettedAllChanges;
    return (
      <div
        key={`notes-key-${pencil.id}`}
        data-testid={`notes-key-${pencil.id}`}
        className="notes-container"
      >
        {get(pencil, 'notification') && (
          <div className="notification">{pencil.notification}</div>
        )}
        {get(pencil, 'salesPersonNote.note') && (
          <div className="sales-person-note">
            <div className="sales-person-timestamp">
              {moment(pencil.salesPersonNote.timestamp).format(
                'MM-DD-YYYY h:mm a',
              )}
            </div>
            <div className="sales-person-note-text">
              {pencil.salesPersonNote.firstName}: {pencil.salesPersonNote.note}
            </div>
          </div>
        )}
        {showManagerNote && (
          <div className="manager-note">
            <div className="manager-timestamp">
              {moment(pencil.managerNote.timestamp).format('MM-DD-YYYY h:mm a')}
            </div>
            <div className="manager-note-text">{pencil.managerNote.note}</div>
          </div>
        )}
        {showChangeLog && (
          <div>
            <div className="manager-timestamp">
              {moment(pencil.sentAt).format('MM-DD-YYYY h:mm a')}
            </div>
            <div className="change-log-bubble">
              <div className="change-log">
                {renderChangeLog(pencil.changeLog)}
              </div>
            </div>
          </div>
        )}
        {get(pencil, 'managerNote') && (showManagerNote || showChangeLog) && (
          <div className="manager-note-name">
            {`${pencil.managerNote.firstName} ${pencil.managerNote.lastName}`}
          </div>
        )}
        {get(pencil, 'status') === PencilStatus.ACCEPTED &&
          pencil.selectedOffer &&
          renderAcceptedNote(pencil)}
        {!isEmpty(pencil?.selectedFinanceResponse) &&
          renderFinanceResponse(pencil)}
      </div>
    );
  };
  return (
    <div>
      {!isEmbeddedView && <div className="notes-header">Notes</div>}
      <div className="data-table-container notes" ref={notesEnd}>
        <div>
          {orderBy(props.pencils, 'updatedAt')
            .filter(showPencilNote)
            .map(p => renderNote(p))}
        </div>
      </div>
      <Input
        id="pencil-manager-note-input"
        data-testid="manager-note-input"
        type="text"
        className="form-control note-input"
        name="managerNote"
        value={props.note}
        onChange={props.onChange}
        placeholder="Add Note"
        onKeyDown={props.onKeyDown}
      />
    </div>
  );
}

Notes.defaultProps = {
  onKeyDown: () => {},
};

Notes.propTypes = {
  deal: PropTypes.object,
  dealer: PropTypes.object,
  isEmbeddedView: PropTypes.bool,
  note: PropTypes.string,
  onChange: PropTypes.func,
  pencils: PropTypes.array,
  pencilId: PropTypes.string,
  onPushToDMS: PropTypes.func,
  onKeyDown: PropTypes.func,
  showOfferAndRicTab: PropTypes.func,
};

export default Notes;
